import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "../layout/MainLayout";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const API_BASE ="https://globizsystems.click";
const getToken = () =>
  localStorage.getItem("token") || localStorage.getItem("agent_token") || "";

function isoToday() {
  return new Date().toISOString().slice(0, 10);
}

export default function PromiseToPay() {
  const navigate = useNavigate();
  const token = useMemo(() => getToken(), []);

  const api = useMemo(
    () =>
      axios.create({
        baseURL: API_BASE,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }),
    [token]
  );

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const today = isoToday();

  const load = async () => {
    if (!token) {
      await Swal.fire({
        icon: "error",
        title: "Sesi tamat",
        text: "Token tiada. Sila login semula.",
      });
      return;
    }
    try {
      setLoading(true);
      const res = await api.get("/api/agent/ptp/my");
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      await Swal.fire({
        icon: "error",
        title: "Gagal",
        text: e?.response?.data?.error || "Gagal load PTP list.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const decorated = useMemo(() => {
    const list = rows.map((r) => {
      const promised = r.promisedDate || r.promised_date;
      const status = (r.status || "").toUpperCase();
      const overdue = status === "OPEN" && promised && promised < today;

      // snapshot fields from backend
      const nomborAkaun = r.nomborAkaun || r.nombor_akaun || "-";
      const kawasan = r.kawasan || "-";
      const telefon = r.telefon || "";

      const amount = r.promisedAmount ?? r.promised_amount ?? null;

      return { ...r, promised, status, overdue, nomborAkaun, kawasan, telefon, amount };
    });

    // overdue first, then promisedDate ascending
    list.sort((a, b) => {
      if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
      if ((a.promised || "") !== (b.promised || "")) return (a.promised || "").localeCompare(b.promised || "");
      return (b.id || 0) - (a.id || 0);
    });

    return list;
  }, [rows, today]);

  const openCustomer = (bil) => {
    if (!bil) return;
    navigate("/renter-detail", { state: { qrResult: String(bil) } });
  };

  return (
    <MainLayout>
      <div className="max-w-3xl mx-auto bg-white shadow-md rounded-lg p-6 mt-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Promise To Pay</h2>
            <p className="text-xs text-gray-500 mt-1">
              Overdue (merah) akan ditunjukkan di atas.
            </p>
          </div>
          <button
            onClick={load}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        <div className="mt-4 space-y-2">
          {decorated.length === 0 ? (
            <p className="text-sm text-gray-500">Tiada PTP.</p>
          ) : (
            decorated.map((r) => (
              <button
                key={r.id}
                onClick={() => openCustomer(r.bil)}
                className={`w-full text-left rounded-lg border p-3 transition ${
                  r.overdue ? "bg-red-50 border-red-300" : "bg-white border-gray-200 hover:bg-gray-50"
                }`}
              >
                {/* Header line */}
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-gray-800">
                    No Akaun: {r.nomborAkaun}
                  </div>
                  <div className={`text-xs font-bold ${r.overdue ? "text-red-600" : "text-gray-600"}`}>
                    {r.overdue ? "OVERDUE" : r.status}
                  </div>
                </div>

                {/* Kawasan */}
                <div className="text-sm text-gray-700 mt-1">
                  Kawasan: <span className="font-semibold">{r.kawasan}</span>
                </div>

                {/* Phone (only if exists) */}
                {r.telefon ? (
                  <div className="text-sm text-gray-700">
                    Telefon: <span className="font-semibold">{r.telefon}</span>
                  </div>
                ) : null}

                {/* Promised date + amount */}
                <div className="text-sm text-gray-700 mt-1">
                  Tarikh Janji Bayar: <span className="font-semibold">{r.promised || "-"}</span>
                </div>

                <div className="text-sm text-gray-700">
                  Jumlah:{" "}
                  <span className="font-semibold">
                    {r.amount === null || r.amount === undefined || r.amount === "" ? "-" : r.amount}
                  </span>
                </div>

                {/* Note */}
                {r.note ? (
                  <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                    Nota: {r.note}
                  </div>
                ) : null}

                {/* Internal ref */}
                <div className="text-xs text-gray-500 mt-2">
                  BIL: {r.bil}
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </MainLayout>
  );
}